import React, { useState, useEffect, useCallback } from 'react';
import Icon from 'components/AppIcon';
import api from 'lib/api';

export default function AttendanceCalendar({ memberId }) {
  const [attendances, setAttendances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());

  const fetchAttendances = useCallback(async () => {
    if (!memberId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/attendances', { params: { member_id: memberId, limit: 2000 } });
      setAttendances(data ?? []);
    } catch (err) {
      setError('Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => { fetchAttendances(); }, [fetchAttendances]);

  const today = new Date();
  const year = currentDate?.getFullYear();
  const month = currentDate?.getMonth();
  const daysInMonth = new Date(year, month + 1, 0)?.getDate();
  const firstDay = new Date(year, month, 1)?.getDay();

  const checkedInDays = new Set(
    attendances
      ?.filter(a => {
        const d = new Date(a?.check_in_time);
        return d?.getFullYear() === year && d?.getMonth() === month;
      })
      ?.map(a => new Date(a?.check_in_time)?.getDate())
  );

  const allTimeTotal = attendances?.length ?? 0;
  const monthTotal = checkedInDays?.size ?? 0;

  // Calculate current streak (consecutive days from today backwards)
  let streak = 0;
  const allDates = new Set(
    attendances?.map(a => {
      const d = new Date(a?.check_in_time);
      return `${d?.getFullYear()}-${d?.getMonth()}-${d?.getDate()}`;
    })
  );
  for (let i = 0; i <= 365; i++) {
    const d = new Date(today);
    d?.setDate(today?.getDate() - i);
    const key = `${d?.getFullYear()}-${d?.getMonth()}-${d?.getDate()}`;
    if (allDates?.has(key)) streak++;
    else if (i > 0) break;
  }

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const isCurrentMonth = year === today?.getFullYear() && month === today?.getMonth();

  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-5 shadow-[var(--shadow-sm)]">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-[var(--color-foreground)] font-[var(--font-heading)]">Attendance Calendar</h3>
        {streak > 0 && (
          <div className="flex items-center gap-1.5 bg-[var(--color-warning)]/10 px-3 py-1.5 rounded-full">
            <Icon name="Flame" size={14} color="var(--color-warning)" />
            <span className="text-sm font-bold text-[var(--color-warning)] font-[var(--font-caption)]">{streak} day streak</span>
          </div>
        )}
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        {[
          { label: 'This Month', value: monthTotal, icon: 'Calendar' },
          { label: 'Current Streak', value: streak, icon: 'Flame' },
          { label: 'All Time', value: allTimeTotal, icon: 'Trophy' },
        ]?.map(stat => (
          <div key={stat?.label} className="bg-[var(--color-background)] border border-[var(--color-border)] rounded-[var(--radius-sm)] p-3 text-center">
            <p className="text-lg font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">{loading ? '—' : stat?.value}</p>
            <p className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">{stat?.label}</p>
          </div>
        ))}
      </div>

      {/* Month Navigation */}
      <div className="flex items-center justify-between mb-3">
        <button onClick={prevMonth} className="p-1.5 rounded hover:bg-[var(--color-muted)] transition-colors">
          <Icon name="ChevronLeft" size={16} color="var(--color-muted-foreground)" />
        </button>
        <span className="text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-caption)]">
          {currentDate?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </span>
        <button
          onClick={nextMonth}
          disabled={isCurrentMonth}
          className="p-1.5 rounded hover:bg-[var(--color-muted)] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <Icon name="ChevronRight" size={16} color="var(--color-muted-foreground)" />
        </button>
      </div>

      {/* Day Labels */}
      <div className="grid grid-cols-7 mb-1">
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']?.map(d => (
          <div key={d} className="text-center text-[10px] font-semibold text-[var(--color-muted-foreground)] font-[var(--font-caption)] py-1">{d}</div>
        ))}
      </div>

      {/* Calendar Grid */}
      {loading ? (
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: 35 })?.map((_, i) => (
            <div key={i} className="aspect-square rounded bg-[var(--color-muted)] animate-pulse" />
          ))}
        </div>
      ) : error ? (
        <div className="flex items-center justify-center gap-2 py-8">
          <Icon name="AlertCircle" size={16} color="var(--color-error)" />
          <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{error}</p>
        </div>
      ) : !memberId ? (
        <div className="flex flex-col items-center justify-center gap-2 py-8">
          <Icon name="Calendar" size={28} color="var(--color-muted-foreground)" />
          <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">No attendance data available</p>
        </div>
      ) : (
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: firstDay })?.map((_, i) => <div key={`e-${i}`} />)}
          {Array.from({ length: daysInMonth })?.map((_, i) => {
            const day = i + 1;
            const isToday = isCurrentMonth && day === today?.getDate();
            const isFuture = isCurrentMonth && day > today?.getDate();
            const checked = checkedInDays?.has(day);
            return (
              <div
                key={day}
                className={`aspect-square rounded-[var(--radius-sm)] flex items-center justify-center text-xs font-medium font-[var(--font-caption)] transition-all duration-150 ${
                  isToday ? 'ring-2 ring-[var(--color-primary)] ring-offset-1' : ''
                } ${
                  isFuture ? 'text-[var(--color-muted-foreground)]/30' :
                  checked ? 'bg-[var(--color-primary)] text-white' : 'text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]'
                }`}
              >
                {checked && !isFuture ? (
                  <span className="flex flex-col items-center leading-none">
                    <span className="text-[9px]">✓</span>
                    <span>{day}</span>
                  </span>
                ) : day}
              </div>
            );
          })}
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-4 mt-4">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-[var(--color-primary)]" />
          <span className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Checked In</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-[var(--color-muted)]" />
          <span className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)]">No Visit</span>
        </div>
      </div>
    </div>
  );
}
